/**
 * Deterministic per-email avatar palette shared by the thread surface
 * (PRD 001 §7.3 / §8). The participant stack and the in-thread
 * message-list avatars consume the same hash + slot list so a sender's
 * avatar colour is identical in both places.
 */
export const AVATAR_COLOR_SLOTS = [
  "purple",
  "teal",
  "coral",
  "amber",
  "blue",
] as const;

export type AvatarColorSlot = (typeof AVATAR_COLOR_SLOTS)[number];

function hashEmail(email: string): number {
  const normalized = (email ?? "").trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash * 31 + normalized.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/**
 * Returns the slot name (one of {@link AVATAR_COLOR_SLOTS}) for the
 * given email. Consumers map the slot to their own BEM modifier class
 * — both the participant-stack lib and the admin message-list use
 * `--<slot>` (e.g. `--purple`).
 */
export function avatarColorSlotForEmail(email: string): AvatarColorSlot {
  return AVATAR_COLOR_SLOTS[hashEmail(email) % AVATAR_COLOR_SLOTS.length];
}

/**
 * True initials for an avatar — first letter of each whitespace-
 * separated word in the display name, up to two letters. Single-word
 * names yield a single letter. When no display name is available, falls
 * back to the first letter of the email local-part. Returns `"?"` when
 * neither input is usable.
 *
 * Examples:
 * - `"Bradley Brotherson"` → `"BB"`
 * - `"Aaron Romano"` → `"AR"`
 * - `"Staff"` → `"S"`
 * - `null` + `"bbrotherson@gmail.com"` → `"B"`
 */
export function avatarInitialsForParticipant(
  displayName: string | null | undefined,
  email: string,
): string {
  const name = (displayName ?? "").trim();
  if (name !== "") {
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return words[0][0].toUpperCase();
  }
  const localPart = (email ?? "").trim().split("@")[0] ?? "";
  return localPart === "" ? "?" : localPart[0].toUpperCase();
}
